import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Hooks and Utils
import useFieldEvents from '@core/hooks/cohesion/useFieldEvents';
// Reducers and Actions
import { selectFormCorrelationId, selectStepContext } from '@core/reducers/eventingSlice';
import { selectInputObject, setInput } from '@core/reducers/inputsSlice';
import removeDuplicates from '@core/utils/arrays/removeDuplicates';

import type { TOptionGroupField } from './types';

const MultiOptionGroupField: TOptionGroupField = ({ field, heading, InputComponent, fieldNumber }) => {
  // Action Dispatcher
  const dispatch = useDispatch();

  // Form Correlation Id for events
  const formCorrelationId = useSelector(selectFormCorrelationId);
  // stepContext for for fields events
  const stepContext = useSelector(selectStepContext);

  const { fieldSelected, fieldViewed } = useFieldEvents({
    correlationId: formCorrelationId,
    userInputField: {
      autofilled: false,
      fieldName: field.name,
      fieldLabel: heading,
      fieldType: field.type,
      fieldNumber,
    },
  });

  useEffect(() => {
    fieldViewed();
  }, []);

  // Inputs State properties
  const fieldInputObject = useSelector((state) => selectInputObject(state, field.name));

  const isSelected = useCallback(
    (selectedOptions, option) => !!selectedOptions?.find((selectedOption) => selectedOption.label === option.label),
    []
  );

  const handleSelection = useCallback(
    (option) => {
      fieldSelected({
        userInputField: {
          fieldValue: option.label,
        },
      });

      // If the option was previously selected, we remove it from the selection
      if (isSelected(fieldInputObject?.options, option)) {
        // We need this to support options with the same value
        const newOptions = fieldInputObject?.options?.filter((selectedOption) => selectedOption.label !== option.label);
        const newValue = newOptions.map((selectedOption) => selectedOption.value);

        // If the array is empty, we set the value and options to undefined
        // to indicate that the user hasn't made a selection
        const payload =
          newValue?.length === 0
            ? { key: field.name, value: undefined, options: undefined }
            : { key: field.name, value: newValue, options: newOptions };

        dispatch(setInput(payload));
        return;
      }

      // If user already has selected the max amount of options allowed, we don't allow more selections
      if (field.max && fieldInputObject?.options?.length >= field.max) {
        return;
      }

      // If option wasn't previously selected, we add it as long as we aren't over the max
      const newValue = fieldInputObject?.value
        ? removeDuplicates([...fieldInputObject.value, option.value])
        : [option.value];
      const newOptions = fieldInputObject?.options ? [...fieldInputObject.options, option] : [option];

      dispatch(
        setInput({
          key: field.name,
          value: newValue,
          options: newOptions,
        })
      );
    },
    [fieldInputObject, field, heading, isSelected, stepContext]
  );

  return (
    <InputComponent
      field={field}
      selection={fieldInputObject?.options}
      handleSelection={handleSelection}
      isSelected={isSelected}
    />
  );
};

export default MultiOptionGroupField;
